"use client";

import * as React from "react";
import { Dialog as D } from "radix-ui";
import type { LucideIcon } from "lucide-react";
import { ChevronLeft, ChevronRight, LoaderCircle, Search, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/controls";
import { useNav } from "./nav";

/* =====================================================================
 * Primitivos do app mobile — toque grande (≥44px), áreas seguras,
 * transições curtas, sem hover.
 * ===================================================================== */

/** Página com barra superior fixa, rolagem própria e pull-to-refresh opcional. */
export function Page({
  title,
  subtitle,
  back,
  actions,
  onRefresh,
  children,
  footer,
  className,
  fill,
  leading,
}: {
  /** Conteúdo gerencia a própria rolagem (telas desktop hospedadas). */
  fill?: boolean;
  /** Elemento à esquerda do título (ex.: logotipo da empresa na tela inicial). */
  leading?: React.ReactNode;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  back?: boolean;
  actions?: React.ReactNode;
  onRefresh?: () => Promise<unknown> | unknown;
  children: React.ReactNode;
  footer?: React.ReactNode;
  className?: string;
}) {
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const [pull, setPull] = React.useState(0);
  const [refreshing, setRefreshing] = React.useState(false);
  const [dragging, setDragging] = React.useState(false);
  const start = React.useRef<number | null>(null);
  const LIMITE = 68;

  const onTouchStart = (e: React.TouchEvent) => {
    if (!onRefresh || refreshing) return;
    start.current = (scrollRef.current?.scrollTop ?? 0) <= 0 ? e.touches[0].clientY : null;
    setDragging(start.current !== null);
  };
  const onTouchMove = (e: React.TouchEvent) => {
    if (start.current === null) return;
    const d = e.touches[0].clientY - start.current;
    setPull(d > 0 ? Math.min(96, d * 0.5) : 0);
  };
  const onTouchEnd = async () => {
    if (start.current === null) return;
    start.current = null;
    setDragging(false);
    if (pull >= LIMITE * 0.6 && onRefresh) {
      setRefreshing(true);
      setPull(44);
      try {
        await onRefresh();
      } finally {
        setRefreshing(false);
        setPull(0);
      }
    } else setPull(0);
  };

  return (
    <div className={cn("flex h-full min-h-0 flex-col bg-app", className)}>
      <header className="shrink-0 border-b border-border bg-surface pt-[env(safe-area-inset-top)]">
        <div className="flex h-14 items-center gap-1 px-2">
          {back ? (
            <button
              type="button"
              aria-label="Voltar"
              onClick={() => useNav.getState().back()}
              className="inline-flex size-11 items-center justify-center rounded-full text-fg active:bg-row-hover"
            >
              <ChevronLeft className="size-6" />
            </button>
          ) : leading ? (
            <div className="ml-1 mr-2 flex shrink-0 items-center">{leading}</div>
          ) : (
            <span className="w-2" />
          )}
          <div className="min-w-0 flex-1">
            <h1 className="truncate text-[17px] leading-6 font-semibold">{title}</h1>
            {subtitle ? <p className="truncate text-xs text-muted">{subtitle}</p> : null}
          </div>
          {actions ? <div className="flex items-center">{actions}</div> : null}
        </div>
      </header>
      {fill ? (
        <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden">{children}</div>
      ) : (
      <div
        ref={scrollRef}
        className="relative min-h-0 flex-1 overflow-y-auto overscroll-contain"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {onRefresh ? (
          <div className="pointer-events-none absolute inset-x-0 top-0 flex justify-center" style={{ height: pull, opacity: Math.min(1, pull / LIMITE) }}>
            <span className="mt-2 inline-flex size-8 items-center justify-center rounded-full border border-border bg-surface shadow-popover">
              <LoaderCircle className={cn("size-4 text-primary", refreshing && "animate-spin")} style={refreshing ? undefined : { transform: `rotate(${pull * 4}deg)` }} />
            </span>
          </div>
        ) : null}
        <div style={{ transform: pull ? `translateY(${pull}px)` : undefined, transition: dragging ? undefined : "transform 150ms" }}>{children}</div>
      </div>
      )}
      {footer}
    </div>
  );
}

export function IconButton({ icon: Icon, label, onClick, badge }: { icon: LucideIcon; label: string; onClick: () => void; badge?: number }) {
  return (
    <button type="button" aria-label={label} onClick={onClick} className="relative inline-flex size-11 items-center justify-center rounded-full text-fg active:bg-row-hover">
      <Icon className="size-[22px]" />
      {badge ? (
        <span className="absolute top-1.5 right-1.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-danger px-1 text-2xs font-semibold text-primary-fg tnum">{badge}</span>
      ) : null}
    </button>
  );
}

/** Seção com título pequeno em caixa alta. */
export function Section({ title, action, children, className }: { title?: string; action?: React.ReactNode; children: React.ReactNode; className?: string }) {
  return (
    <section className={cn("px-4 pt-5", className)}>
      {title ? (
        <div className="mb-2 flex items-center justify-between">
          <h2 className="text-xs font-semibold tracking-wide text-muted uppercase">{title}</h2>
          {action}
        </div>
      ) : null}
      {children}
    </section>
  );
}

export function Card({ children, className, onClick }: { children: React.ReactNode; className?: string; onClick?: () => void }) {
  const Comp = onClick ? "button" : "div";
  return (
    <Comp
      {...(onClick ? { type: "button" as const, onClick } : {})}
      className={cn("block w-full rounded-xl border border-border bg-surface text-left", onClick && "transition-transform duration-100 active:scale-[0.985] active:bg-row-hover", className)}
    >
      {children}
    </Comp>
  );
}

/** Logotipo da empresa (Sistema › Empresa) ou a inicial do nome fantasia. */
export function AvatarEmpresa({ empresa, className }: { empresa: { nomeFantasia: string; logo?: string | null }; className?: string }) {
  return (
    <span className={cn("inline-flex shrink-0 items-center justify-center overflow-hidden rounded-xl border border-border bg-surface", className)} aria-hidden>
      {empresa.logo ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img src={empresa.logo} alt="" className="size-full object-contain p-1" />
      ) : (
        <span className="text-base font-bold text-primary">{empresa.nomeFantasia.trim().charAt(0).toUpperCase()}</span>
      )}
    </span>
  );
}

/** Lista agrupada estilo iOS/Android. */
export function List({ children, className }: { children: React.ReactNode; className?: string }) {
  return <div className={cn("divide-y divide-border overflow-hidden rounded-xl border border-border bg-surface", className)}>{children}</div>;
}

export function Row({
  icon: Icon,
  title,
  subtitle,
  trailing,
  onClick,
  chevron = !!onClick,
  danger,
  leading,
}: {
  icon?: LucideIcon;
  /** Substitui o ícone por um elemento próprio (ex.: logotipo). */
  leading?: React.ReactNode;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  trailing?: React.ReactNode;
  onClick?: () => void;
  chevron?: boolean;
  danger?: boolean;
}) {
  const Comp = onClick ? "button" : "div";
  return (
    <Comp {...(onClick ? { type: "button" as const, onClick } : {})} className={cn("flex min-h-13 w-full items-center gap-3 px-4 py-2.5 text-left", onClick && "active:bg-row-hover")}>
      {leading ? (
        leading
      ) : Icon ? (
        <span className={cn("inline-flex size-9 shrink-0 items-center justify-center rounded-lg bg-surface-2 text-muted", danger && "bg-danger-soft text-danger")}>
          <Icon className="size-[18px]" />
        </span>
      ) : null}
      <span className="min-w-0 flex-1">
        <span className={cn("block truncate text-[15px] leading-5", danger && "text-danger")}>{title}</span>
        {subtitle ? <span className="block truncate text-[13px] leading-5 text-muted">{subtitle}</span> : null}
      </span>
      {trailing}
      {chevron ? <ChevronRight className="size-5 shrink-0 text-subtle" /> : null}
    </Comp>
  );
}

/** Chips horizontais roláveis (filtros). */
export function Chips<T extends string>({ value, onChange, options }: { value: T; onChange: (v: T) => void; options: { value: T; label: string; count?: number; tom?: "danger" | "warning" }[] }) {
  return (
    <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1 [scrollbar-width:none]">
      {options.map((o) => {
        const on = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            onClick={() => onChange(o.value)}
            className={cn(
              "inline-flex h-9 shrink-0 items-center gap-1.5 rounded-full border px-3.5 text-sm font-medium transition-colors duration-100",
              on ? "border-primary bg-primary text-primary-fg" : "border-border bg-surface text-fg active:bg-row-hover",
            )}
          >
            {o.tom && !on ? <span className={cn("size-1.5 rounded-full", o.tom === "danger" ? "bg-danger" : "bg-warning")} /> : null}
            {o.label}
            {o.count !== undefined ? <span className={cn("text-xs tnum", on ? "opacity-80" : "text-subtle")}>{o.count}</span> : null}
          </button>
        );
      })}
    </div>
  );
}

export function SearchField({ value, onChange, placeholder = "Buscar", autoFocus }: { value: string; onChange: (v: string) => void; placeholder?: string; autoFocus?: boolean }) {
  return (
    <label className="flex h-11 items-center gap-2 rounded-xl border border-border bg-surface px-3 focus-within:border-focus">
      <Search className="size-[18px] text-subtle" />
      <input
        value={value}
        autoFocus={autoFocus}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="h-full min-w-0 flex-1 bg-transparent text-base outline-none placeholder:text-subtle"
        enterKeyHint="search"
      />
      {value ? (
        <button type="button" aria-label="Limpar" onClick={() => onChange("")} className="inline-flex size-7 items-center justify-center rounded-full bg-neutral-soft text-muted">
          <X className="size-4" />
        </button>
      ) : null}
    </label>
  );
}

/** Bottom sheet (folha inferior). */
export function Sheet({ open, onOpenChange, title, children }: { open: boolean; onOpenChange: (v: boolean) => void; title?: string; children: React.ReactNode }) {
  return (
    <D.Root open={open} onOpenChange={onOpenChange}>
      <D.Portal>
        <D.Overlay className="fixed inset-0 z-50 bg-overlay [animation:m-fade_120ms_ease-out]" />
        <D.Content
          aria-describedby={undefined}
          className="fixed inset-x-0 bottom-0 z-50 max-h-[85dvh] overflow-y-auto rounded-t-2xl border-t border-border bg-surface pb-[max(16px,env(safe-area-inset-bottom))] outline-none [animation:m-sheet_150ms_ease-out]"
        >
          <div className="sticky top-0 bg-surface pt-2">
            <div className="mx-auto h-1 w-10 rounded-full bg-border-strong" />
            {title ? <D.Title className="px-5 pt-3 pb-2 text-[17px] font-semibold">{title}</D.Title> : <D.Title className="sr-only">Opções</D.Title>}
          </div>
          <div className="px-4 pt-1">{children}</div>
        </D.Content>
      </D.Portal>
    </D.Root>
  );
}

/** Botão grande de ação (rodapé de detalhe, sheets). */
export function BigButton({
  children,
  onClick,
  variant = "secondary",
  disabled,
  loading,
  icon: Icon,
}: {
  children: React.ReactNode;
  onClick?: () => void;
  variant?: "primary" | "secondary" | "danger";
  disabled?: boolean;
  loading?: boolean;
  icon?: LucideIcon;
}) {
  return (
    <button
      type="button"
      disabled={disabled || loading}
      onClick={onClick}
      className={cn(
        "inline-flex h-12 w-full items-center justify-center gap-2 rounded-xl px-4 text-[15px] font-semibold transition-transform duration-100 active:scale-[0.98] disabled:opacity-45",
        variant === "primary" && "bg-primary text-primary-fg",
        variant === "secondary" && "border border-border-strong bg-surface text-fg",
        variant === "danger" && "border border-border-strong bg-surface text-danger",
      )}
    >
      {loading ? <LoaderCircle className="size-5 animate-spin" /> : Icon ? <Icon className="size-5" /> : null}
      {children}
    </button>
  );
}

export function ListSkeleton({ rows = 6, tall }: { rows?: number; tall?: boolean }) {
  return (
    <div className="flex flex-col gap-2.5">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className={cn("rounded-xl border border-border bg-surface p-4", tall ? "h-28" : "h-16")}>
          <Skeleton className="h-3 w-1/3" />
          <Skeleton className="mt-2.5 h-3" style={{ width: `${55 + ((i * 13) % 35)}%` }} />
        </div>
      ))}
    </div>
  );
}

export function MEmpty({ icon: Icon, text, action }: { icon: LucideIcon; text: string; action?: React.ReactNode }) {
  return (
    <div className="flex flex-col items-center gap-3 px-6 py-16 text-center">
      <span className="inline-flex size-12 items-center justify-center rounded-full bg-surface-2 text-subtle">
        <Icon className="size-6" />
      </span>
      <p className="text-[15px] text-muted">{text}</p>
      {action}
    </div>
  );
}

/** Pares rótulo/valor em lista vertical. */
export function KV({ items }: { items: { label: string; value: React.ReactNode }[] }) {
  return (
    <List>
      {items.map((it) => (
        <div key={it.label} className="flex min-h-11 items-center justify-between gap-4 px-4 py-2.5">
          <span className="shrink-0 text-sm text-muted">{it.label}</span>
          <span className="min-w-0 truncate text-right text-[15px] tnum">{it.value ?? "—"}</span>
        </div>
      ))}
    </List>
  );
}

/** Carrega mais itens ao chegar no fim da lista. */
export function useInfinite<T>(items: T[] | undefined, page = 30) {
  const [n, setN] = React.useState(page);
  const sentinel = React.useRef<HTMLDivElement>(null);
  const total = items?.length ?? 0;
  React.useEffect(() => {
    const el = sentinel.current;
    if (!el) return;
    const io = new IntersectionObserver((e) => {
      if (e[0].isIntersecting) setN((x) => x + page);
    });
    io.observe(el);
    return () => io.disconnect();
  }, [page, total]);
  return { visiveis: items?.slice(0, n), sentinel: n < total ? <div ref={sentinel} className="h-10" /> : null, reset: () => setN(page) };
}
